import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import AppInput from "../../Core/AppInput/AppInput";
import AppText from "../../Core/AppText/AppText";
import Button, { ButtonStyleProps } from "../../Core/Button/Button";
import Flex from "../../Layout/Flex";
import Competence from "../../../models/Competence";
import { getCompetencesLabels } from "../../../utils/competence.utils";
import { formatCurrencyString } from "../../../utils/string.utils";
import { useOrganisation } from "../../../networking/organisation.hooks";
import { useConfirmModalStore } from "../../../stores/confirmModal.store";

interface SupplementValueModalStyles {
	primaryActionButton?: ButtonStyleProps;
	secondaryActionButton?: ButtonStyleProps;
}

type SupplementValueModalProps = {
	onCancelCallBack?: Function;
	onUpdateSupplementValue?: Function;
	value?: Competence;
	hourlyPay?: number;
	holidayPay?: number;
};

const SupplementValueModal: React.FC<SupplementValueModalProps> = ({
	onCancelCallBack,
	onUpdateSupplementValue,
	value,
	hourlyPay,
	holidayPay,
}) => {
	const modalStore = useConfirmModalStore();
	const { modalData } = modalStore;
	const { data: organisation } = useOrganisation();

	const supplementVal = value?.supplement;

	const [supplementValue, setSupplementValue] = useState<number>(supplementVal || 0);

	const { t } = useTranslation();

	const [modalStyle, setModalStyle] = useState<SupplementValueModalStyles>();

	const handleOnCancel = () => {
		onCancelCallBack && onCancelCallBack();
	};

	useEffect(() => {
		const defaultGrayButton: ButtonStyleProps = {
			backgroundColor: "dividerGray",
			textColor: "black",
		};

		switch (modalData.type) {
			case "DANGER":
				setModalStyle({
					primaryActionButton: {
						backgroundColor: "danger",
						textColor: "white",
					},
					secondaryActionButton: {
						...defaultGrayButton,
					},
				});
				break;
			default:
				setModalStyle({
					secondaryActionButton: {
						...defaultGrayButton,
					},
				});
		}
	}, [modalData.type]);

	const storeSupplementValue = () => {
		onUpdateSupplementValue && onUpdateSupplementValue(Number(supplementValue));
		onCancelCallBack && onCancelCallBack();
	};

	const { branch, service } = getCompetencesLabels(organisation?.competences || [], {
		branch: (value?.branch || "") as string,
		service: (value?.service || "") as string,
	});

	const totalHourlyPay = useMemo(() => {
		return Number(hourlyPay || 0) + Number(supplementValue || 0);
	}, [hourlyPay, supplementValue]);

	const totalHourlyPayInclHolidayPay = useMemo(() => {
		return (
			(Number(hourlyPay || 0) + Number(supplementValue || 0)) *
			(holidayPay ? holidayPay / 100 + 1 : 1)
		);
	}, [hourlyPay, supplementValue, holidayPay]);

	return (
		<ModalContainer>
			<ModalWrapper>
				<AppText size="xm" weight="bold">
					{branch + ", " + service}
				</AppText>
				<Flex margin="1.5rem 0" direction="column">
					<Flex alignItems="center" justifyContent="center">
						<AppText>{t("consultant.salary.hourlySupplement")}</AppText>
						<AppInput
							type="number"
							onChangeNumber={setSupplementValue}
							value={supplementValue}
							containerMargin="0.3rem 0 0 0.5rem"
							containerWidth="50px"
						/>
						<AppText margin="0 0 0 0.25rem">SEK</AppText>
					</Flex>
					<Flex alignItems="center" justifyContent="center">
						<AppText textAlign="center" margin="2rem 0" color="grayText">
							{t("consultant.salary.hourlySupplementTot", {
								tot: formatCurrencyString(totalHourlyPay),
							})}
							<br />
							{t("consultant.salary.hourlySupplementTotWithHolidayPay", {
								tot: formatCurrencyString(totalHourlyPayInclHolidayPay),
							})}
						</AppText>
					</Flex>
				</Flex>

				<Button
					width="12.625rem"
					{...modalStyle?.primaryActionButton}
					margin="0 0 0.5rem 0"
					onClick={() => storeSupplementValue()}
				>
					{t("SAVE")}
				</Button>
				<Button width="12.625rem" {...modalStyle?.secondaryActionButton} onClick={handleOnCancel}>
					{t("CANCEL")}
				</Button>
			</ModalWrapper>
		</ModalContainer>
	);
};
export default SupplementValueModal;

const ModalContainer = styled.div`
	position: fixed;
	z-index: 1000;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	background: rgba(0, 0, 0, 0.6);
	overflow-y: auto;
`;

const ModalWrapper = styled.section`
	background: white;
	width: 100%;
	max-width: 32.5rem;
	margin: 10em auto 5em auto;
	height: auto;
	align-items: center;
	border-radius: 10px;
	display: flex;
	flex-direction: column;
	padding: 3.125rem;
`;
